<script setup lang="ts">
  import { AddRoleModal, RecipientEntityTypeEnum, Role } from '@gohighlevel/ghl-proposals-common'
  import { PropType, computed } from 'vue'
  import { useAppStore } from '@/store/app'
  import { useBuilderStore } from '@/store/builder'
  import { useNotify } from '../../../composition/useNotify'
  import { useI18n } from 'vue-i18n'

  const props = defineProps({
    show: {
      type: Boolean,
      default: false,
    },
    // When set, the modal edits this role instead of adding a new one.
    role: {
      type: Object as PropType<Role | null>,
      default: null,
    },
  })
  const emits = defineEmits(['update:show', 'added'])

  const { t } = useI18n()
  const appStore = useAppStore()
  const builderStore = useBuilderStore()
  const { handleErrorMessage } = useNotify()

  // Names of other roles (excludes the role being edited) so the form can validate uniqueness inline.
  const otherRoleNames = computed(() =>
    (appStore.document?.roles || []).filter(r => !(props.role && r.name === props.role.name)).map(r => r.name)
  )

  const onSubmitRole = (roleData: Role) => {
    const isEdit = !!props.role
    // When editing, exclude the role being edited from the duplicate checks.
    const otherRoles = (appStore.document?.roles || []).filter(r => !(isEdit && r.name === props.role?.name))

    const isDuplicateName = otherRoles.some(r => r.name.trim().toLowerCase() === roleData.name.trim().toLowerCase())
    if (isDuplicateName) {
      handleErrorMessage(`${t('common.roleName')}: ${roleData.name}`)
      return
    }

    const isDuplicateEmail = otherRoles.some(r => r.email.trim().toLowerCase() === roleData.email.trim().toLowerCase())
    if (isDuplicateEmail) {
      handleErrorMessage(t('common.duplicateRoleEmail'))
      return
    }

    if (isEdit && props.role) {
      builderStore.updateRole(props.role.name, roleData)
    } else {
      appStore.addRole(roleData)
    }
    // Emit for both add and edit so the opener re-assigns/re-fetches the element against the role.
    // For add this auto-assigns the new role; for edit it forces the property-tray card to re-read
    // the (just-updated) role so the shown details refresh immediately while the tray stays open.
    emits('added', `${roleData.name}_${RecipientEntityTypeEnum.ROLE}`)
    emits('update:show', false)
  }
</script>

<template>
  <AddRoleModal
    :show="props.show"
    :role="props.role"
    :existing-role-names="otherRoleNames"
    @on-cancel="emits('update:show', false)"
    @on-success="onSubmitRole"
  />
</template>
